// Stop one kind of email, from the link at the foot of an email
// (PLAN-2026-10-18-families-and-newcomers §7.1, scenario K3).
//
// `/stop-email?k=<key>&t=<type>`. The key is the proof, so nobody signs in:
// the link works from whatever inbox it lands in. The page says what it stops
// in the settings screen's own words, and stops it with one press — never on
// opening, because mail scanners open every link in a message and would stop
// it for everybody whose provider looks first. A mail provider's own button
// stops it without this page at all (RFC 8058).

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { EmailStopView, type EmailStopShape } from '@fsp/domain';
import { get, messageOf, send } from '../data/api';
import { useTier } from '../shell/useTier';
import { Wizard, type WizardStep } from '../templates/Wizard';
import { Text } from '../kit/ui';

type Stage = 'reading' | 'ask' | 'stopped' | 'off' | 'failed';

export function StopEmailScreen() {
  const tier = useTier();
  const navigate = useNavigate();
  const location = useLocation();
  const [{ key, type }] = useState(() => {
    const q = new URLSearchParams(location.search);
    return { key: q.get('k') ?? '', type: q.get('t') ?? '' };
  });
  const [stage, setStage] = useState<Stage>('reading');
  const [view, setView] = useState<EmailStopShape | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const path = `/email/stop?k=${encodeURIComponent(key)}&t=${encodeURIComponent(type)}`;

  useEffect(() => {
    void (async () => {
      const reply = await get(path, EmailStopView);
      if (reply.status === 'ok') {
        setView(reply.data);
        setStage(reply.data.on ? 'ask' : 'off');
        return;
      }
      setProblem(
        messageOf(
          reply,
          'That link did not work.',
          'Could not reach the site. Check your connection.',
        ),
      );
      setStage('failed');
    })();
  }, [path]);

  const stop = async () => {
    if (pending) return;
    setPending(true);
    const reply = await send('POST', path, {}, EmailStopView);
    setPending(false);
    if (reply.status === 'ok') {
      setView(reply.data);
      setStage('stopped');
      return;
    }
    setProblem(
      messageOf(
        reply,
        'That did not work. Try again.',
        'Could not reach the site. Check your connection.',
      ),
    );
  };

  const label = view?.label ?? '';
  const steps: Record<Stage, WizardStep> = {
    reading: {
      id: 'reading',
      title: 'Stop these emails',
      content: (
        <Text tone="secondary" size="sm" block>
          One moment.
        </Text>
      ),
      doneLabel: 'Reading…',
    },
    ask: {
      id: 'ask',
      title: 'Stop these emails?',
      hint: `Emails for “${label}” stop for the account this one was sent to.`,
      content: (
        <Text tone="secondary" size="sm" block>
          It still shows in the bell, and you can turn its email back on in Account → Notifications.
        </Text>
      ),
      error: problem,
      doneLabel: pending ? 'Stopping…' : 'Stop these emails',
    },
    stopped: {
      id: 'stopped',
      title: 'Stopped',
      hint: `No more emails for “${label}”. It still shows in the bell, and Account → Notifications turns it back on.`,
      content: null,
      doneLabel: 'Done',
    },
    off: {
      id: 'off',
      title: 'These emails are off',
      hint: `Emails for “${label}” are off already. Account → Notifications turns them back on.`,
      content: null,
      doneLabel: 'Done',
    },
    failed: {
      id: 'failed',
      title: 'That link will not work',
      hint: problem ?? 'That link did not work.',
      content: (
        <Text tone="secondary" size="sm" block>
          Every email from your leagues can be turned off in Account → Notifications, once you sign
          in.
        </Text>
      ),
      doneLabel: 'Done',
    },
  };

  const home = () => navigate('/', { replace: true });
  return (
    <div className="fsp-frame" data-tier={tier}>
      <Wizard
        title="Your email"
        steps={[steps[stage]]}
        index={0}
        onIndex={() => undefined}
        onDone={() => {
          if (stage === 'ask') void stop();
          else if (stage !== 'reading') home();
        }}
        // Cancel only while there is something to decline.
        onCancel={stage === 'ask' ? home : undefined}
      />
    </div>
  );
}
